import React from "react";
import { Phone, MessageCircle, Send, Mail, AtSign } from "lucide-react";
import { Field, Input, Select, Toggle, ChipGroup, SectionTitle } from "../primitives";
import {
  CONTACT_CHANNELS, RESPONSE_TIMES, CONTACT_VISIBILITY, LANGUAGES, COUNTRY_CODES,
} from "../constants";

const CHANNEL_ICONS = {
  phone:    Phone,
  whatsapp: MessageCircle,
  telegram: Send,
  viber:    Phone,
  email:    Mail,
  in_app:   AtSign,
};

/**
 * Reusable phone-with-country-code input used for phone / whatsapp / viber.
 */
function PhoneWithCC({ value, onChange, testid, placeholder = "67 123 456" }) {
  const isCustom = value.country_code &&
    !COUNTRY_CODES.find(c => c.code === value.country_code);
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <Select
        value={isCustom ? "__custom__" : value.country_code}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ ...value, country_code: v === "__custom__" ? "+" : v });
        }}
        data-testid={`${testid}-cc-select`}
      >
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.code}>{`${c.code} ${c.label}`}</option>
        ))}
        <option value="__custom__">Other…</option>
      </Select>
      {isCustom && (
        <Input
          value={value.country_code}
          onChange={(e) => onChange({ ...value, country_code: e.target.value })}
          placeholder="+###"
          className="col-span-2"
          data-testid={`${testid}-cc-custom`}
        />
      )}
      <Input
        type="tel"
        inputMode="tel"
        value={value.number}
        onChange={(e) => onChange({ ...value, number: e.target.value })}
        placeholder={placeholder}
        className={isCustom ? "col-span-2" : ""}
        data-testid={`${testid}-number`}
      />
    </div>
  );
}

export default function Step8Contact({ form, set }) {
  const prefs = form.contact_prefs || {};
  const det = form.contact_details || {};
  const togglePref = (k, v) =>
    set({ contact_prefs: { ...prefs, [k]: v } });
  const setDetail = (k, patch) =>
    set({ contact_details: { ...det, [k]: { ...(det[k] || {}), ...patch } } });

  return (
    <div className="space-y-6">
      <SectionTitle icon={Phone} title="Contact preferences"
        subtitle="Pick the channels you use and enter the details clients should reach you on." />

      <Field label="Channels" required hint="Enable a channel and fill in the matching field below.">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CONTACT_CHANNELS.map((c) => {
            const Icon = CHANNEL_ICONS[c.k] || Phone;
            return (
              <div key={c.k} className="px-3 py-2.5 rounded-lg bg-stone-900/50 border border-stone-800 flex items-center gap-2">
                <Icon className="w-4 h-4 text-amber-400 flex-none" />
                <Toggle
                  checked={!!prefs[c.k]}
                  onChange={(v) => togglePref(c.k, v)}
                  label={c.label}
                  testid={`contact-${c.k}`}
                />
              </div>
            );
          })}
        </div>
      </Field>

      {/* Conditional inputs — only shown when matching toggle is on */}
      {prefs.phone && (
        <Field label="Phone number" required testid="phone-input">
          <PhoneWithCC value={det.phone} onChange={(v) => setDetail("phone", v)} testid="contact-phone" />
        </Field>
      )}
      {prefs.whatsapp && (
        <Field label="WhatsApp number" required testid="whatsapp-input">
          <PhoneWithCC value={det.whatsapp} onChange={(v) => setDetail("whatsapp", v)} testid="contact-whatsapp" />
        </Field>
      )}
      {prefs.telegram && (
        <Field label="Telegram username" required hint="With or without @ — we will normalise it.">
          <Input
            value={det.telegram?.username || ""}
            onChange={(e) => setDetail("telegram", { username: e.target.value })}
            placeholder="@username"
            data-testid="contact-telegram-username"
          />
        </Field>
      )}
      {prefs.viber && (
        <Field label="Viber number" required testid="viber-input">
          <PhoneWithCC value={det.viber} onChange={(v) => setDetail("viber", v)} testid="contact-viber" />
        </Field>
      )}
      {prefs.email && (
        <Field label="Email address" required>
          <Input
            type="email"
            value={det.email?.address || ""}
            onChange={(e) => setDetail("email", { address: e.target.value })}
            placeholder="you@example.com"
            data-testid="contact-email-address"
          />
        </Field>
      )}

      <Field label="Languages for communication">
        <ChipGroup
          options={LANGUAGES}
          value={form.contact_languages}
          onChange={(contact_languages) => set({ contact_languages })}
          testidPrefix="contact-lang"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Typical response time">
          <Select
            value={form.response_time}
            onChange={(e) => set({ response_time: e.target.value })}
            data-testid="contact-response"
          >
            {RESPONSE_TIMES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </Select>
        </Field>
        <Field label="Who can see my contact details">
          <Select
            value={form.contact_visibility}
            onChange={(e) => set({ contact_visibility: e.target.value })}
            data-testid="contact-visibility"
          >
            {CONTACT_VISIBILITY.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
          </Select>
        </Field>
      </div>
    </div>
  );
}
